import { saveSearchTerm } from "~/features/search/api/saveSearchTerm";
import type { SrpProductData } from "~/utils/models";
import { forwardRef, type ReactNode } from "react";
import { ProductContext } from "~/domains/product/ProductContext";
import { Product } from "~/domains/product/Product";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { LinkType } from "~/common/components/Link";
import { useIsMinDesktopSizeM } from "~/common/hooks/useMediaQuery";
import { styled } from "~/common/mui";
import { createAutosuggestProductUrl } from "~/features/search/utils/searchNavigationUrls";

interface Props {
  className?: string;
  product: SrpProductData;
}

export function resolveProductUrlAutosuggest(product: SrpProductData) {
  return createAutosuggestProductUrl(product);
}

export const ProductItem = forwardRef<HTMLAnchorElement, Props>(({ product, className }: Props, ref) => {
  const hasSales = Object.keys(product.salesCampaign).length > 0;

  const pdpUrl = resolveProductUrlAutosuggest(product);

  const linkProps = {
    onClick: () => saveSearchTerm(product.designation, pdpUrl),
    href: pdpUrl,
    ["tracking-aux-sch-prod-name"]: product.designation,
    ["tracking-aux-sch-prod-id"]: product.sapMaterialNumber,
    ["data-prod-article"]: product.hybrisCode,
  };

  const isDesktopM = useIsMinDesktopSizeM();

  return (
    <ProductContext value={product}>
      <ProductItemView
        className={className}
        hasSales={hasSales}
        campaignCode={Object.values(product.salesCampaign)[0]?.[0].code}
        image={isDesktopM && <Product.PrimaryImage linkProps={{ ...linkProps, tabIndex: -1 }} />}
        designation={
          <>
            <Product.FullDesignation
              designationComponent={"span"}
              linkProps={{ ...linkProps, ref, variant: LinkType.NAVIGATION }}
            />
            {hasSales && <SaleTag size={16} />}
          </>
        }
        description={<Product.ShortDescription linkProps={{ ...linkProps, tabIndex: -1 }} />}
      />
    </ProductContext>
  );
});

interface ProductItemViewProps {
  className?: string;
  hasSales?: boolean;
  campaignCode?: string;
  image?: ReactNode;
  designation: ReactNode;
  description: ReactNode;
}

export const ProductItemView = ({
  className,
  hasSales = false,
  campaignCode = "None",
  image,
  designation,
  description,
}: ProductItemViewProps) => {
  return (
    <ProductItemRoot
      className={className}
      asgsta-product-parent-marker="true"
      asgsta-is-campaign-belonging={`${hasSales}`}
      asgsta-campaign={campaignCode ?? "None"}
      data-zoovu="searchSuggestion"
    >
      {image && <ImageSlot>{image}</ImageSlot>}
      <DesignationContainer>{designation}</DesignationContainer>
      <DescriptionSlot>{description}</DescriptionSlot>
    </ProductItemRoot>
  );
};

const truncatedContent = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const ProductItemRoot = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  display: "grid",
  gridTemplate: `"designation designation" 1fr "description description" 1fr / ${theme.spacing(8)} minmax(0, auto)`,
  columnGap: theme.spacing(2),
  overflow: "hidden",
  [theme.breakpoints.up("desktop-m")]: {
    gridTemplate: `"image designation" 1fr "image description" 1fr / ${theme.spacing(8)} minmax(0, auto)`,
  },
}));

const ImageSlot = styled("div")(({ theme }) => ({
  gridArea: "image",
  height: theme.spacing(8),
  width: theme.spacing(8),
  "img, a": {
    objectFit: "contain",
    height: "100%",
    width: "100%",
  },
}));

const DesignationContainer = styled("div")(({ theme }) => ({
  gridArea: "designation",
  alignSelf: "flex-end",
  fontWeight: "bold",
  ...theme.typography.bodyMedium,
  span: {
    ...truncatedContent,
    "> a": {
      display: "inline",
    },
  },
}));

const DescriptionSlot = styled("div")(({ theme }) => ({
  gridArea: "description",
  alignSelf: "flex-start",
  display: "inline-flex",
  ...truncatedContent,
  ...theme.typography.bodySmall,
  a: {
    overflow: "hidden",
    "> span": truncatedContent,
  },
}));
